import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { deleteMenu, getMenuByCategoryId } from "../Services/MenuApi";
import { getCategorieById } from "../Services/CategrieApi";
import { getOrganisationById } from "../Services/OrganizationApi";
import GifLoader from "../Components/loader";
import { useTranslation } from "react-i18next";
import { useOrganisations } from "../Auth/useOrganisations";
import swal from "sweetalert";

// Custom CSS for the "Modern Feel"
const styles = `
  .menu-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 20px;
    padding: 20px;
  }

  .modern-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 15px;
    text-align: center;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    transition: all 0.3s ease;
    border: 1px solid #f0f0f0;
    cursor: pointer;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .modern-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  }

  .img-wrapper {
    width: 100px;
    height: 100px;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 8px; /* Slightly rounded corners for the image like your screenshot */
  }

  .img-wrapper img {
    width: 100%;
    height: 100%;
    object-fit: cover; /* This ensures the image fills the square perfectly */
  }

  .item-title {
    font-size: 14px;
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 8px;
    min-height: 40px; /* Ensures alignment even with long names */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .item-price {
    font-size: 15px;
    font-weight: 700;
    color: #10b981; /* That nice green from your screenshot */
  }

  /* Hidden action buttons that appear on hover */
  .card-actions {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    opacity: 0;
    transition: opacity 0.2s ease;
    border-radius: 12px;
  }

  .modern-card:hover .card-actions {
    opacity: 1;
  }
`;

function CategoryMenus() {
  const { t } = useTranslation();
  const { id: categoryId } = useParams();
  const navigate = useNavigate();

  const [menus, setMenus] = useState([]);
  const [category, setCategory] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  const [organisations, setOrganisations, usedOrganisations] =
    useOrganisations();

  const apiBaseUrl = useMemo(
    () => process.env.REACT_APP_API_BASE_URL || "",
    [],
  );

  // --- Logic remains the same (Keep your existing API calls) ---
  const handleApiError = useCallback(
    (error, customMessage = "An error occurred") => {
      setError(customMessage);
      swal({ title: t("Error"), text: customMessage, icon: "error" });
    },
    [t],
  );

  const fetchCategory = useCallback(async () => {
    try {
      const response = await getCategorieById(categoryId);
      if (response?.data?.success) setCategory(response.data.data);
    } catch (error) {
      handleApiError(error, t("Failed to load category"));
    }
  }, [categoryId, handleApiError, t]);

  const fetchMenus = useCallback(async () => {
    try {
      const response = await getMenuByCategoryId(categoryId);
      setMenus(response?.data?.success ? response.data.data : []);
    } catch (error) {
      handleApiError(error, t("Failed to load menus"));
    }
  }, [categoryId, handleApiError, t]);

  const fetchOrganisation = useCallback(async () => {
    if (!usedOrganisations) return;
    try {
      const response = await getOrganisationById(usedOrganisations);
      if (response?.data?.success === "1")
        setCurrency(response.data.data.currency);
    } catch (error) {
      handleApiError(error, t("Failed to load organisation data"));
    } finally {
      setLoading(false);
    }
  }, [usedOrganisations, handleApiError, t]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.allSettled([
        fetchCategory(),
        fetchMenus(),
        fetchOrganisation(),
      ]);
    };
    loadData();
  }, [
    categoryId,
    usedOrganisations,
    refreshKey,
    fetchCategory,
    fetchMenus,
    fetchOrganisation,
  ]);

  const handleDeleteMenu = async (menuId) => {
    const willDelete = await swal({
      title: t("Are you sure?"),
      icon: "warning",
      buttons: [t("No"), t("Yes")],
      dangerMode: true,
    });
    if (willDelete) {
      try {
        const response = await deleteMenu(menuId);
        if (response?.data?.success) {
          swal(t("Deleted!"), { icon: "success" });
          setRefreshKey((p) => p + 1);
        }
      } catch (e) {
        handleApiError(e, t("Error deleting menu!"));
      }
    }
  };

  const getImageUrl = (menu) => {
    if (imageErrors[menu.id] || !menu.image)
      return "images/product/no-image.png";
    return `${apiBaseUrl}${menu.image}`;
  };

  // --- Modern Components ---

  const ActionButton = ({ icon, onClick, color, tooltip }) => (
    <button
      className={`btn btn-${color}-light btn-circle-modern shadow-sm`}
      onClick={onClick}
      title={t(tooltip)}
    >
      <i className={`fa fa-${icon}`}></i>
    </button>
  );

  return (
    <section className="content">
      <style>{styles}</style>
      <GifLoader loading={loading} />

      {/* Header Section */}
      <div className="content-header mb-4">
        <div className="d-flex justify-content-between align-items-center bg-white p-4 rounded shadow-sm">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1 bg-transparent p-0">
                <li className="breadcrumb-item">
                  <Link to="/categories">{t("Categories")}</Link>
                </li>
                <li className="breadcrumb-item active">
                  {category?.libelle?.fr || t("Menus")}
                </li>
              </ol>
            </nav>
            <h2 className="font-weight-bold mb-0 text-dark">
              {category?.libelle?.fr
                ? t("{{category}}", { category: category.libelle.fr })
                : t("Category Menus")}
            </h2>
          </div>
          <Link to="/menus" className="btn btn-primary-light px-4 shadow-sm">
            <i className="fa fa-arrow-left mr-2"></i> {t("Articles")}
          </Link>
        </div>
      </div>

      {/* Grid */}
      <div className="menu-grid">
        {menus.map((menu) => (
          <div key={menu.id} className="modern-card">
            {/* 1. Image Section */}
            <div className="img-wrapper">
              <img
                src={
                  menu.image.length
                    ? getImageUrl(menu.image)
                    : "images/product/no-image.png"
                }
                alt={menu.titre?.fr}
              />
            </div>

            {/* 2. Text Section */}
            <div className="item-title">{menu.titre?.fr}</div>

            {/* 3. Price Section */}
            <div className="item-price">
              {menu.prix_ttc} <small>{currency || "TND"}</small>
            </div>

            {/* 4. Action Overlay (Edit/Delete/View) */}
            <div className="card-actions">
              <button
                className="btn btn-circle btn-info-light btn-sm"
                onClick={() => selectedMenu(menu)}
              >
                <i className="fa fa-eye"></i>
              </button>
              <button
                className="btn btn-circle btn-warning-light btn-sm"
                onClick={() => navigate(`/menu/edit/${menu.id}`)}
              >
                <i className="fa fa-pencil"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modern Modal */}
      {selectedMenu && (
        <div
          className="modal show fade modal-glass"
          style={{ display: "block" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: "20px" }}
            >
              <div className="modal-body p-0">
                <button
                  className="close"
                  onClick={() => setSelectedMenu(null)}
                  style={{
                    position: "absolute",
                    right: "20px",
                    top: "20px",
                    zIndex: 10,
                    fontSize: "2rem",
                  }}
                >
                  {" "}
                  &times;{" "}
                </button>

                <div className="row no-gutters">
                  <div className="col-md-5">
                    <img
                      src={getImageUrl(selectedMenu)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "images/product/no-image.png";
                      }}
                      className="h-100 w-100"
                      style={{
                        objectFit: "cover",
                        minHeight: "400px",
                        borderTopLeftRadius: "20px",
                        borderBottomLeftRadius: "20px",
                      }}
                    />
                  </div>
                  <div className="col-md-7 p-5">
                    <span className="badge badge-success-light mb-2">
                      {category?.libelle?.fr}
                    </span>
                    <h2 className="font-weight-bold mb-3">
                      {selectedMenu.titre?.fr}
                    </h2>
                    <h3 className="text-primary mb-4">
                      {selectedMenu.prix_ttc} {currency}
                    </h3>

                    <h6 className="text-uppercase font-weight-bold text-muted small">
                      {t("Description")}
                    </h6>
                    <p className="text-dark mb-4">
                      {selectedMenu.description?.fr}
                    </p>

                    {selectedMenu.ingredients?.length > 0 && (
                      <div className="mb-3">
                        <h6 className="text-uppercase font-weight-bold text-muted small">
                          {t("Ingredients")}
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedMenu.ingredients.map((ing, i) => (
                            <span
                              key={i}
                              className="badge badge-secondary-light mr-2 mb-2"
                            >
                              {ing}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div
                className="modal-footer bg-light border-0 px-5 py-3"
                style={{
                  borderBottomLeftRadius: "20px",
                  borderBottomRightRadius: "20px",
                }}
              >
                <button
                  className="btn btn-outline-secondary px-4"
                  onClick={() => setSelectedMenu(null)}
                >
                  {t("Close")}
                </button>
                <button
                  className="btn btn-primary px-4"
                  onClick={() => navigate(`/menu/edit/${selectedMenu.id}`)}
                >
                  <i className="fa fa-edit mr-2"></i> {t("Edit Menu")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default CategoryMenus;
